/**
 * Occupation Interface
 * Represents structured occupation information
 * 
 * @interface Occupation
 * @author Financial Insights Platform
 * @version 1.0
 */

export interface Occupation {
  /**
   * Type of occupation
   * @type {string}
   * @values "self-employed" | "employed" | "business-owner" | "student" | "retired" | "other"
   */
  occupationType?: string;

  /**
   * Company name (required for employed/business-owner)
   * @type {string}
   */
  company?: string;

  /**
   * Job title (required for employed/business-owner)
   * @type {string}
   */
  jobTitle?: string;

  /**
   * Additional details about occupation
   * @type {string}
   */
  details?: string;
}

/**
 * Type guard to check if occupation requires company and job title
 * @param occupation Occupation object
 * @returns true if occupation type is "employed" or "business-owner"
 */
export function requiresCompanyAndJobTitle(occupation: Occupation): boolean {
  return occupation.occupationType === "employed" || occupation.occupationType === "business-owner";
}

/**
 * Validate occupation data
 * @param occupation Occupation object
 * @returns true if occupation data is valid
 */
export function isValidOccupation(occupation: Occupation): boolean {
  // If occupation type requires company/job title, ensure they are present
  if (requiresCompanyAndJobTitle(occupation)) {
    return !!(occupation.company?.trim() && occupation.jobTitle?.trim());
  }
  
  // For other occupation types, just having a type is enough
  return !!occupation.occupationType?.trim();
}

/**
 * Format occupation type for display
 * @param type Raw occupation type
 * @returns Formatted occupation type
 */
export function formatOccupationType(type?: string): string {
  if (!type) return "Not specified";
  
  switch (type) {
    case "self-employed":
      return "Self Employed";
    case "employed":
      return "Employed";
    case "business-owner":
      return "Business Owner";
    case "student":
      return "Student";
    case "retired":
      return "Retired";
    case "other":
      return "Other";
    default:
      return type;
  }
}

/**
 * Get formatted occupation as a single string
 * @param occupation Occupation object
 * @returns Formatted occupation string
 */
export function getFormattedOccupation(occupation?: Occupation): string {
  if (!occupation) return "Not specified";
  
  const parts: string[] = [];
  
  // Add occupation type
  if (occupation.occupationType) {
    parts.push(formatOccupationType(occupation.occupationType));
  }
  
  // Add job title if present
  if (occupation.jobTitle?.trim()) {
    parts.push(occupation.jobTitle);
  }
  
  // Add company if present
  if (occupation.company?.trim()) {
    const separator = parts.length > 0 ? " at " : "";
    parts.push(separator + occupation.company);
  }
  
  // Add details if present and no other info
  if (parts.length === 0 && occupation.details?.trim()) {
    parts.push(occupation.details);
  }
  
  return parts.length > 0 ? parts.join(" - ") : "Not specified";
}

/**
 * Get short description of occupation
 * @param occupation Occupation object
 * @returns Short occupation description
 */
export function getShortOccupationDescription(occupation?: Occupation): string {
  if (!occupation) return "Not specified";
  
  if (occupation.jobTitle?.trim()) {
    return occupation.jobTitle;
  }
  
  if (occupation.occupationType) {
    return formatOccupationType(occupation.occupationType);
  }
  
  return "Not specified";
}
